import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, Save, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export function CadastrarImpostoPage() {
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState({
    nome: '',
    tipo: '',
    valor: '',
    dataVencimento: '',
    competencia: '',
    status: 'pendente',
    observacoes: ''
  })

  const [isLoading, setIsLoading] = useState(false)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.nome || !formData.tipo || !formData.valor || !formData.dataVencimento || !formData.competencia) {
      alert('Por favor, preencha todos os campos obrigatórios.')
      return
    }

    setIsLoading(true)

    try {
      const novoImposto = {
        nome: formData.nome,
        tipo: formData.tipo,
        valor: parseFloat(formData.valor),
        dataVencimento: formData.dataVencimento,
        dataPagamento: formData.status === 'pago' ? new Date().toISOString().split('T')[0] : null,
        competencia: formData.competencia,
        status: formData.status as 'pendente' | 'pago' | 'vencido' | 'isento',
        observacoes: formData.observacoes || undefined
      }

      // Aqui você salvaria no store
      console.log('Novo imposto:', novoImposto)
      
      // Redirecionar para a listagem
      navigate('/financas/administracao/impostos')
    } catch (error) {
      console.error('Erro ao cadastrar imposto:', error)
      alert('Erro ao cadastrar imposto. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/financas/administracao/impostos')
  }

  // Gerar opções de competência (últimos 12 meses + próximos 12 meses)
  const gerarOpcoesCompetencia = () => {
    const opcoes = []
    const hoje = new Date()
    
    for (let i = -12; i <= 12; i++) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() + i, 1)
      const ano = data.getFullYear()
      const mes = (data.getMonth() + 1).toString().padStart(2, '0')
      const valor = `${ano}-${mes}`
      const label = `${mes}/${ano}`
      opcoes.push({ valor, label })
    }
    
    return opcoes
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Cadastrar Novo Imposto</h1>
          <p className="text-muted-foreground">
            Registre um novo imposto ou obrigação tributária
          </p>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <CardHeader>
          <CardTitle>Informações do Imposto</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Imposto *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: IRPJ - Imposto de Renda Pessoa Jurídica"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo *</Label>
                <Select 
                  value={formData.tipo} 
                  onValueChange={(value) => handleInputChange('tipo', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Federal">Federal</SelectItem>
                    <SelectItem value="Estadual">Estadual</SelectItem>
                    <SelectItem value="Municipal">Municipal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="valor">Valor *</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={formData.valor}
                  onChange={(e) => handleInputChange('valor', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataVencimento">Data de Vencimento *</Label>
                <Input
                  id="dataVencimento"
                  type="date"
                  value={formData.dataVencimento}
                  onChange={(e) => handleInputChange('dataVencimento', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="competencia">Competência *</Label>
                <Select 
                  value={formData.competencia} 
                  onValueChange={(value) => handleInputChange('competencia', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a competência" />
                  </SelectTrigger>
                  <SelectContent>
                    {gerarOpcoesCompetencia().map((opcao) => (
                      <SelectItem key={opcao.valor} value={opcao.valor}>
                        {opcao.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => handleInputChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="pago">Pago</SelectItem>
                    <SelectItem value="vencido">Vencido</SelectItem>
                    <SelectItem value="isento">Isento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Observações adicionais sobre o imposto (opcional)"
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                rows={3}
              />
            </div>

            {/* Informações adicionais baseadas no status */}
            {formData.status === 'pago' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>Atenção:</strong> Ao marcar como "Pago", a data de pagamento será automaticamente definida como hoje.
                </p>
              </div>
            )}

            {formData.status === 'isento' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Informação:</strong> Impostos marcados como "Isento" não entrarão nos cálculos de valores pendentes.
                </p>
              </div>
            )}

            {/* Botões */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isLoading ? 'Salvando...' : 'Salvar Imposto'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}